import { useState, useCallback } from 'react'
import { toast } from 'react-toastify'
import { axiosInstance } from '../../../../helpers'
import { Application, CompanyApplication } from '../../../../models/Company'
import { Company } from '../../../../models/Company'

const BASE = '/beta/api/company'

export interface ApplicationCreateUpdatePayload {
    name: string
    slug: string
    description?: string
    short_description?: string
    is_active?: boolean
    default_price?: string
}

/** List all applications. */
export function useApplicationList(activeOnly?: boolean) {
    const [list, setList] = useState<Application[]>([])
    const [loading, setLoading] = useState(false)

    const fetch = useCallback(async () => {
        setLoading(true)
        try {
            const res = await axiosInstance.get<Application[]>(
                `${BASE}/applications/`,
                {
                    params:
                        activeOnly != null ? { active_only: activeOnly } : {},
                },
            )
            setList(res.data)
            return res.data
        } catch (e: any) {
            const status = e?.response?.status
            const msg = e?.response?.data?.error || e?.response?.data?.message
            if (status === 403) {
                toast.error(
                    'You do not have permission to manage applications.',
                )
            } else {
                toast.error(msg || 'Failed to load applications')
            }
            throw e
        } finally {
            setLoading(false)
        }
    }, [activeOnly])

    return { list, loading, fetch }
}

/** Create application (admin only). */
export function useCreateApplication(onSuccess?: () => void) {
    const [loading, setLoading] = useState(false)

    const create = useCallback(
        async (payload: ApplicationCreateUpdatePayload) => {
            setLoading(true)
            try {
                await axiosInstance.post<Application>(
                    `${BASE}/applications/`,
                    payload,
                )
                toast.success('Application created')
                onSuccess?.()
            } catch (e: any) {
                const msg =
                    e?.response?.data?.error ||
                    e?.response?.data?.slug?.[0] ||
                    e?.response?.data?.name?.[0] ||
                    e?.response?.data?.message
                toast.error(msg || 'Failed to create application')
                throw e
            } finally {
                setLoading(false)
            }
        },
        [onSuccess],
    )

    return { create, loading }
}

/** Get one application (admin only). */
export function useApplicationDetail(id: number | null) {
    const [app, setApp] = useState<Application | null>(null)
    const [loading, setLoading] = useState(false)

    const fetch = useCallback(async () => {
        if (id == null) return null
        setLoading(true)
        try {
            const res = await axiosInstance.get<Application>(
                `${BASE}/applications/${id}/`,
            )
            setApp(res.data)
            return res.data
        } catch (e: any) {
            if (e?.response?.status === 403) {
                toast.error(
                    'You do not have permission to view this application.',
                )
            } else if (e?.response?.status === 404) {
                toast.error('Application not found')
            } else {
                toast.error(
                    e?.response?.data?.message || 'Failed to load application',
                )
            }
            throw e
        } finally {
            setLoading(false)
        }
    }, [id])

    return { app, loading, fetch }
}

/** Update application (admin only). */
export function useUpdateApplication(onSuccess?: () => void) {
    const [loading, setLoading] = useState(false)

    const update = useCallback(
        async (
            id: number,
            payload: Partial<ApplicationCreateUpdatePayload>,
        ) => {
            setLoading(true)
            try {
                await axiosInstance.patch<Application>(
                    `${BASE}/applications/${id}/`,
                    payload,
                )
                toast.success('Application updated')
                onSuccess?.()
            } catch (e: any) {
                const msg =
                    e?.response?.data?.error || e?.response?.data?.message
                toast.error(msg || 'Failed to update application')
                throw e
            } finally {
                setLoading(false)
            }
        },
        [onSuccess],
    )

    return { update, loading }
}

/** Delete application (admin only). */
export function useDeleteApplication(onSuccess?: () => void) {
    const [loading, setLoading] = useState(false)

    const remove = useCallback(
        async (id: number) => {
            setLoading(true)
            try {
                await axiosInstance.delete(`${BASE}/applications/${id}/`)
                toast.success('Application deleted')
                onSuccess?.()
            } catch (e: any) {
                if (e?.response?.status === 403) {
                    toast.error(
                        'You do not have permission to delete applications.',
                    )
                } else {
                    toast.error(
                        e?.response?.data?.message ||
                            'Failed to delete application',
                    )
                }
                throw e
            } finally {
                setLoading(false)
            }
        },
        [onSuccess],
    )

    return { remove, loading }
}

/** Admin company list (companies with applications array). */
export interface AdminCompany extends Company {
    applications: CompanyApplication[]
}

export interface AdminCompanyListResponse {
    count: number
    next: string | null
    previous: string | null
    results: AdminCompany[]
}

const DEFAULT_PAGE_SIZE = 25
const MAX_PAGE_SIZE = 100

export function useAdminCompanyList() {
    const [list, setList] = useState<AdminCompany[]>([])
    const [count, setCount] = useState(0)
    const [next, setNext] = useState<string | null>(null)
    const [previous, setPrevious] = useState<string | null>(null)
    const [loading, setLoading] = useState(false)

    const fetch = useCallback(
        async (opts?: {
            page?: number
            page_size?: number
            user_email?: string
            /** Search in company name or email (case-insensitive contains). */
            search?: string
        }) => {
            const page = opts?.page ?? 1
            const pageSize = Math.min(
                MAX_PAGE_SIZE,
                Math.max(1, opts?.page_size ?? DEFAULT_PAGE_SIZE),
            )
            setLoading(true)
            try {
                const params: Record<string, string | number> = {
                    page,
                    page_size: pageSize,
                }
                if (opts?.user_email != null) {
                    params.user_email = opts.user_email
                }
                if (opts?.search != null && opts.search.trim() !== '') {
                    params.search = opts.search.trim()
                }
                const res = await axiosInstance.get<AdminCompanyListResponse>(
                    `${BASE}/admin-company-manage/`,
                    { params },
                )
                const data = res.data
                setList(Array.isArray(data.results) ? data.results : [])
                setCount(typeof data.count === 'number' ? data.count : 0)
                setNext(data.next ?? null)
                setPrevious(data.previous ?? null)
                return data
            } catch (e: any) {
                if (e?.response?.status === 403) {
                    toast.error(
                        'You do not have permission to view company access.',
                    )
                } else {
                    toast.error(
                        e?.response?.data?.message ||
                            'Failed to load companies',
                    )
                }
                throw e
            } finally {
                setLoading(false)
            }
        },
        [],
    )

    return { list, count, next, previous, loading, fetch }
}

/** Grant application to company. Optional custom_price in body. */
export function useGrantApplicationToCompany(onSuccess?: () => void) {
    const [loading, setLoading] = useState(false)

    const grant = useCallback(
        async (
            companyId: number,
            applicationId: number,
            customPrice?: string | null,
        ) => {
            setLoading(true)
            try {
                await axiosInstance.post(
                    `${BASE}/${companyId}/applications/${applicationId}/`,
                    customPrice != null
                        ? { custom_price: customPrice }
                        : undefined,
                )
                toast.success('Application granted to company')
                onSuccess?.()
            } catch (e: any) {
                toast.error(
                    e?.response?.data?.error ||
                        e?.response?.data?.message ||
                        'Failed to grant application',
                )
                throw e
            } finally {
                setLoading(false)
            }
        },
        [onSuccess],
    )

    return { grant, loading }
}

/** Set or clear custom price for company–application. */
export function useSetCompanyApplicationPrice(onSuccess?: () => void) {
    const [loading, setLoading] = useState(false)

    const setPrice = useCallback(
        async (
            companyId: number,
            applicationId: number,
            customPrice: string | null,
        ) => {
            setLoading(true)
            try {
                await axiosInstance.patch(
                    `${BASE}/${companyId}/applications/${applicationId}/`,
                    { custom_price: customPrice },
                )
                toast.success(
                    customPrice != null
                        ? 'Custom price set'
                        : 'Custom price cleared',
                )
                onSuccess?.()
            } catch (e: any) {
                toast.error(
                    e?.response?.data?.message || 'Failed to update price',
                )
                throw e
            } finally {
                setLoading(false)
            }
        },
        [onSuccess],
    )

    return { setPrice, loading }
}

/** Revoke application from company. */
export function useRevokeApplicationFromCompany(onSuccess?: () => void) {
    const [loading, setLoading] = useState(false)

    const revoke = useCallback(
        async (companyId: number, applicationId: number) => {
            setLoading(true)
            try {
                await axiosInstance.delete(
                    `${BASE}/${companyId}/applications/${applicationId}/`,
                )
                toast.success('Application revoked from company')
                onSuccess?.()
            } catch (e: any) {
                toast.error(
                    e?.response?.data?.message ||
                        'Failed to revoke application',
                )
                throw e
            } finally {
                setLoading(false)
            }
        },
        [onSuccess],
    )

    return { revoke, loading }
}
